"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

interface Options {
  intervalMs?: number; // poll the endpoint on an interval (silent refresh)
  staleMs?: number;    // reuse cached data without a network call if fresher than this
}

// ---------------------------------------------------------------------------
// Shared response cache (stale-while-revalidate) + in-flight de-duplication.
//
// The same endpoint is used by many screens at once — `account` alone is read by the sidebar
// credit badge, the dashboard, billing and the account page. Without a shared cache each mount
// fired its own request and every navigation re-fetched from scratch (skeleton flash on every
// page change). Now:
//   • cached data renders IMMEDIATELY on mount, then revalidates in the background;
//   • concurrent reads of the same path share ONE network request;
//   • a fresh result fans out to every mounted component on that path, so the credit badge and
//     the page you're on never disagree.
// ---------------------------------------------------------------------------
type Entry = { data: unknown; ts: number };

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();
const subscribers = new Map<string, Set<(data: unknown) => void>>();

// Cap the response cache. Keys include every filter/offset combination a screen has visited, so
// an unbounded Map grew for the whole life of an admin session. Map preserves insertion order,
// so delete-then-set makes each write an LRU touch and eviction is just `the first key`.
const CACHE_MAX_ENTRIES = 200;

function publish(path: string, data: unknown) {
  cache.delete(path);
  cache.set(path, { data, ts: Date.now() });

  while (cache.size > CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    cache.delete(oldest.value);
  }

  const subs = subscribers.get(path);
  if (subs) for (const fn of subs) fn(data);
}

// ---------------------------------------------------------------------------
// Shared per-path pollers.
//
// Polling used to be one setInterval per component. Four separate components poll `metrics`
// (sidebar, alert centre, dashboard, gateway) at 10-15s each, and independent timers drift apart,
// so each one produced its own request — roughly three snapshot computations per window instead
// of one, against a status GROUP BY over the whole message queue.
//
// Now each PATH has at most one timer, running at the shortest interval any subscriber asked for.
// One response fans out to every mounted component via publish(). The timer is also suspended
// while the tab is hidden, with a single visibility listener per path instead of one per
// component.
// ---------------------------------------------------------------------------
interface PollerState {
  timer: ReturnType<typeof setInterval> | null;
  refs: Map<object, number>; // subscriber token -> its requested interval
  onVisibility: () => void;
}

const pollers = new Map<string, PollerState>();

function restartPollTimer(path: string, state: PollerState) {
  if (state.timer) {
    clearInterval(state.timer);
    state.timer = null;
  }

  let shortest = Infinity;
  for (const ms of state.refs.values()) if (ms < shortest) shortest = ms;
  if (!Number.isFinite(shortest)) return;

  state.timer = setInterval(() => {
    if (!document.hidden) void fetchShared(path).catch(() => {});
  }, shortest);
}

function registerPoll(path: string, token: object, intervalMs: number) {
  let state = pollers.get(path);

  if (!state) {
    const created: PollerState = {
      timer: null,
      refs: new Map(),
      onVisibility: () => {
        const current = pollers.get(path);
        if (!current) return;
        if (document.hidden) {
          if (current.timer) {
            clearInterval(current.timer);
            current.timer = null;
          }
        } else {
          void fetchShared(path).catch(() => {}); // refresh immediately on return
          restartPollTimer(path, current);
        }
      }
    };
    document.addEventListener('visibilitychange', created.onVisibility);
    pollers.set(path, created);
    state = created;
  }

  state.refs.set(token, intervalMs);
  restartPollTimer(path, state);
}

function unregisterPoll(path: string, token: object) {
  const state = pollers.get(path);
  if (!state) return;

  state.refs.delete(token);

  if (state.refs.size === 0) {
    if (state.timer) clearInterval(state.timer);
    document.removeEventListener('visibilitychange', state.onVisibility);
    pollers.delete(path);
    return;
  }

  restartPollTimer(path, state);
}

// Fetch `path`, collapsing concurrent callers onto a single request.
function fetchShared<T>(path: string): Promise<T> {
  const existing = inflight.get(path);
  if (existing) return existing as Promise<T>;

  const p = api<T>(path)
    .then((res) => {
      publish(path, res);
      return res;
    })
    .finally(() => {
      inflight.delete(path);
    });

  inflight.set(path, p);
  return p as Promise<T>;
}

/** Drop cached responses so the next read hits the network. Pass a prefix to scope it. */
export function invalidate(prefix?: string) {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

/** Clear everything (call on logout so the next user never sees stale data). */
export function clearFetchCache() {
  cache.clear();
  inflight.clear();
}

/**
 * Pre-fetch static endpoints into the shared cache so the FIRST navigation to a page that uses them
 * is instant (no request on mount — useFetch paints straight from cache). Call once after sign-in.
 * Fire-and-forget: already-cached/in-flight paths are skipped and errors are swallowed (the page
 * will just fetch normally if a warm-up failed).
 */
export function warmCache(paths: string[]) {
  for (const path of paths) {
    if (!cache.has(path) && !inflight.has(path)) {
      void fetchShared(path).catch(() => {});
    }
  }
}

export function useFetch<T>(path: string | null, options: Options = {}) {
  const { intervalMs, staleMs = 10000 } = options;

  // Seed from cache so a revisit paints instantly instead of flashing a skeleton.
  const seeded = path ? (cache.get(path)?.data as T | undefined) : undefined;
  const [data, setData] = useState<T | null>(seeded ?? null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(path) && seeded === undefined);
  const mounted = useRef(true);
  // Stable identity for this hook instance, used as its key in the shared poller registry.
  const pollToken = useRef({});

  // When `path` changes, immediately switch to the NEW path's cached value (or null). Without this
  // the hook keeps showing the previous path's data until the new request resolves — and if the new
  // path is already cached-fresh, no fetch fires, so it would never update at all. setState during
  // render is the supported "reset state when a prop changes" pattern (React re-renders before commit).
  const prevPath = useRef(path);
  if (prevPath.current !== path) {
    prevPath.current = path;
    setData(path ? ((cache.get(path)?.data as T | undefined) ?? null) : null);
    setError("");
    setLoading(path ? cache.get(path) === undefined : false);
  }

  const load = useCallback(
    async (silent = false) => {
      if (!path) return;
      if (!silent && cache.get(path) === undefined) setLoading(true);
      try {
        const res = await fetchShared<T>(path);
        if (mounted.current) {
          setData(res);
          setError("");
        }
      } catch (e) {
        if (mounted.current) setError((e as Error).message);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [path]
  );

  // Subscribe to this path so any refresh (poll, reload, another screen) updates us too.
  useEffect(() => {
    if (!path) return;
    const onData = (d: unknown) => {
      if (mounted.current) {
        setData(d as T);
        setLoading(false);
      }
    };
    let subs = subscribers.get(path);
    if (!subs) {
      subs = new Set();
      subscribers.set(path, subs);
    }
    subs.add(onData);
    return () => {
      subs!.delete(onData);
      if (subs!.size === 0) subscribers.delete(path);
    };
  }, [path]);

  useEffect(() => {
    mounted.current = true;
    if (path) {
      const entry = cache.get(path);
      const fresh = entry && Date.now() - entry.ts < staleMs;
      // Fresh cache: render it and skip the request entirely. Stale/absent: revalidate
      // (silently when we already have something to show).
      if (!fresh) void load(entry !== undefined);
    }

    // Join the shared timer for this path instead of starting our own. Results arrive through
    // the subscription above, so several components polling the same path cost ONE request.
    const token = pollToken.current;
    if (intervalMs && path) registerPoll(path, token, intervalMs);

    return () => {
      mounted.current = false;
      if (intervalMs && path) unregisterPoll(path, token);
    };
  }, [load, intervalMs, staleMs, path]);

  // reload() always goes to the network and fans the result out to every subscriber.
  const reload = useCallback(() => {
    if (path) cache.delete(path);
    return load(true);
  }, [load, path]);

  return { data, error, loading, reload };
}
